import React, { useCallback, useMemo, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { useSocialPostCore } from "../../../../cores/socialPost";
import { PostImage } from "../../../../cores/socialPost/interfaces/postImage";
import { v4 } from "uuid";
import { getAspectRatio, getBase64, urlToDataURL } from "../../../../cores/utils";
import { SocialProviderRules } from "../../../../cores/socialPost/config";
import AreYouSure from "../../AreYouSure";
import { SOCIAL_POST_TYPES } from "../../../../cores/socialPost/consts/socialPostType.constant";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faImages,
  faCloudUploadAlt,
  faCheck,
} from "@fortawesome/free-solid-svg-icons";
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { CANVA_COMING_SOON } from "../../../../cores/canva/config";
import { fetchCanvaConnection } from "../../../../cores/canva/connectApi";
import { CanvaMark } from "../CanvaMark";
import { CanvaConnectModal } from "./CanvaConnectModal";
import { CanvaDesignPickerModal } from "./CanvaDesignPickerModal";
import { toast } from "react-toastify";
// import { ImageBankButton } from "../creator/ImageBankButton";
import { ImageBankModal } from "../ImageBankModal";
import SocialPostImagesDnd from "../SocialPostImagesDnd";
import { COLOR_PRIMARY } from "../../../../constants/theme";
import { useGlobalCore } from "../../../../cores/globals";
import { ADD_SOCIAL_IMAGE_MODAL } from "../../../../constants/modal.constant";
import { useMeCore } from "../../../../cores/me";
import { Container, Row, Col } from "reactstrap";
import "./ModernImageForm.scss";

interface Props {
  singleImage?: boolean;
}

type ImageSource = "upload" | "canva" | "bank" | null;

type ImageSourceOption = {
  id: ImageSource;
  title: string;
  description: string;
  icon: IconDefinition;
  color: string;
  disabled?: boolean;
  comingSoon?: boolean;
  action: () => void;
};

export default function ModernImageForm(props: Props) {
  const { singleImage = false } = props;
  const intl = useIntl(); 
  const {
    addImages,
    updateImage,
    reorderImages,
    images,
    video,
    // removeVideo,
    selectedPost,
    removePostAllMedia,
    // removeAllImages,
    socialProviders,
  } = useSocialPostCore();
  const { turnOffModal } = useGlobalCore();
  const { companyPlan } = useMeCore();

  const ref = useRef<any>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // const imageBankRef = useRef<any>(null);
  const [selectedSource, setSelectedSource] = useState<ImageSource>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [showImageBankModal, setShowImageBankModal] = useState(false);
  const [showCanvaConnectModal, setShowCanvaConnectModal] = useState(false);
  const [showCanvaDesignPicker, setShowCanvaDesignPicker] = useState(false);

  const maxSizeImage = useMemo(() => {
    if (socialProviders.length === 0) {
      return 5 * 1024 * 1024; // Default 5MB
    }
    const sizes = socialProviders
      .map(({ id }) => SocialProviderRules[id]?.maxImageSize)
      .filter(size => size !== undefined);
    
    if (sizes.length === 0) {
      return 5 * 1024 * 1024; // Default 5MB
    }
    
    return Math.max(...sizes) * 1024 * 1024;
  }, [socialProviders]);

  const onChange = (files: File[], pictures: string[]) => {
    const images: PostImage[] = files.map((file, index): PostImage => {
      const { name, size, lastModified, type = "image/jpeg" } = file;
      const id = v4();
      
      // Create PostImage immediately with default dimensions
      const postImage: PostImage = {
        id,
        lastModified,
        name,
        size,
        type,
        data: pictures[index],
        width: 0, // Will be updated when image loads
        height: 0, // Will be updated when image loads
        aspectRatio: "1:1", // Default aspect ratio
      };

      // Process dimensions asynchronously without blocking UI
      const image = new Image();
      image.onload = () => {
        const { width, height } = image;
        const aspectRatio = getAspectRatio(width, height);
        // Update the image with actual dimensions
        updateImage({ 
          ...postImage, 
          aspectRatio, 
          width, 
          height 
        });
      };
      image.onerror = () => {
        // Keep default dimensions if image fails to load
        console.warn('Failed to load image dimensions for:', name);
      };
      image.src = pictures[index];
      
      return postImage;
    });
    
    // Add images immediately to show them in UI
    addImages(images);
  };

  const removeVideos = useCallback(() => {
    if (selectedPost) {
      removePostAllMedia();
    }
  }, [selectedPost, removePostAllMedia]);

  const applyCanvaExport = useCallback(
    async ({
      exportUrl,
      designTitle,
    }: {
      exportUrl: string;
      designTitle: string;
    }) => {
      const file: any = {
        name: designTitle,
        lastModified: new Date().getTime(),
      };
      const picture: string = await urlToDataURL(exportUrl);
      onChange([file], [picture]);
    },
    [onChange]
  );

  const openCanvaCard = useCallback(async () => {
    if (!companyPlan?.rules.allowCanva) return;
    try {
      const c = await fetchCanvaConnection();
      if (c.connected) {
        setShowCanvaDesignPicker(true);
      } else {
        setShowCanvaConnectModal(true);
      }
    } catch (e: any) {
      toast.error(
        e?.response?.data?.message ||
          intl.formatMessage({ id: "app.canvaConnect.backendError" })
      );
    }
  }, [companyPlan?.rules.allowCanva, intl]);

  const handleImageBankClick = () => {
    // Open Image Bank modal directly
    setShowImageBankModal(true);
  };


  const handleImageBankSelect = (images: PostImage[]) => {
    if (images.length > 0) {
      const image = images[0];
      const file = {
        name: image.name,
        lastModified: image.lastModified,
        size: image.size,
        type: image.type
      } as any;
      onChange([file], [image.data]);
    }
    setShowImageBankModal(false);
  };


  const handleUploadClick = () => {
    // Open file browser directly
    setSelectedSource('upload');
    fileInputRef.current?.click();
  };

  const handleFileInputChange = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const input = e.currentTarget;
    const files = Array.from(input.files || []).filter((file) =>
      file.type.startsWith("image/")
    );

    if (files.length > 0) {
      // Convert each file to data URL so it can be sent correctly to the API
      const pictures = await Promise.all(
        files.map(async (file) => (await getBase64(file)) as string)
      );
      onChange(files, pictures);
    }

    // Reset input value to allow selecting the same file again
    if (input) {
      input.value = "";
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    
    const files = Array.from(e.dataTransfer.files).filter((file) =>
      file.type.startsWith("image/")
    );

    if (files.length > 0) {
      const pictures = await Promise.all(
        files.map(async (file) => (await getBase64(file)) as string)
      );
      onChange(files, pictures);
    }
  };

  const imageSourceOptions: ImageSourceOption[] = [
    {
      id: 'upload' as ImageSource,
      title: intl.formatMessage({ id: "app.modernImageForm.uploadTitle" }),
      description: intl.formatMessage({ id: "app.modernImageForm.uploadDescription" }),
      icon: faCloudUploadAlt,
      color: '#007bff',
      action: handleUploadClick
    },
    {
      id: 'canva' as ImageSource,
      title: intl.formatMessage({ id: "app.modernImageForm.canvaTitle" }),
      description: intl.formatMessage({ id: "app.modernImageForm.canvaDescription" }),
      icon: faImages,
      color: '#00c4cc',
      disabled: !companyPlan?.rules.allowCanva,
      action: () => {
        setSelectedSource('canva');
        openCanvaCard();
      }
    },
    {
      id: 'bank' as ImageSource,
      title: intl.formatMessage({ id: "app.modernImageForm.bankTitle" }),
      description: intl.formatMessage({ id: "app.modernImageForm.bankDescription" }),
      icon: faImages,
      color: '#6c757d',
      action: () => {
        setSelectedSource('bank');
        handleImageBankClick();
      }
    }
  ];

  return (
    <div className="modern-image-form">
      <Container fluid className="h-100">
        <Row className="h-100">
          <Col xs="12">
            <div className="d-flex flex-column h-100">
              {/* Título: el cierre está en la barra del SideModal (evita doble ventana / doble X) */}
              <div className="form-header mb-4 text-left">
                <h3 className="mb-2">{intl.formatMessage({ id: "app.modernImageForm.title" })}</h3>
                <p className="text-muted mb-0">{intl.formatMessage({ id: "app.modernImageForm.subtitle" })}</p>
              </div>

              {/* Warning for video */}
              {(video ||
                (selectedPost &&
                  selectedPost.post.type === SOCIAL_POST_TYPES.VIDEO &&
                  selectedPost.media.length > 0)) && (
                <AreYouSure
                  message={intl.formatMessage({ id: "app.socialMediaPost.warningError" })}
                  onClose={() => turnOffModal(ADD_SOCIAL_IMAGE_MODAL)}
                  onSubmit={removeVideos} 
                />
              )}

              {/* Main Content */}
              {!video &&
                (!selectedPost ||
                  selectedPost?.post.type === SOCIAL_POST_TYPES.IMAGE ||
                  (selectedPost?.post.type === SOCIAL_POST_TYPES.VIDEO &&
                    !selectedPost.media.length)) && (
                  <>
                    {/* Image Source Selection */}
                    <div className="image-sources mb-4">
                      <Row className="g-3">
                        {imageSourceOptions.map((option) => (
                          <Col xs="12" md="4" key={option.id}>
                            {option.id === "canva" ? (
                              <div
                                className={`source-card source-card--canva ${
                                  option.comingSoon ? "source-card--canva-soon" : ""
                                } ${option.disabled ? "disabled" : ""} ${
                                  selectedSource === option.id ? "selected" : ""
                                }`}
                                onClick={
                                  option.disabled || option.comingSoon
                                    ? undefined
                                    : option.action
                                }
                                style={
                                  { "--card-color": option.color } as React.CSSProperties
                                }
                                role="button"
                                tabIndex={
                                  option.disabled || option.comingSoon ? -1 : 0
                                }
                                onKeyDown={(e) => {
                                  if (option.disabled || option.comingSoon) {
                                    return;
                                  }
                                  if (e.key === "Enter" || e.key === " ") {
                                    e.preventDefault();
                                    option.action();
                                  }
                                }}
                              >
                                <div className="canva-source-stack">
                                  <span className="canva-source-mark-wrap" aria-hidden>
                                    <CanvaMark size={48} />
                                  </span>
                                  <div className="canva-source-copy">
                                    <h5 className="source-title mb-2">{option.title}</h5>
                                    <p className="source-description mb-0">{option.description}</p>
                                  </div>
                                </div>
                                {option.disabled && !option.comingSoon && (
                                  <div className="disabled-overlay">
                                    <small>
                                      {intl.formatMessage({
                                        id: "app.modernImageForm.insufficientPlan",
                                      })}
                                    </small>
                                  </div>
                                )}
                              </div>
                            ) : (
                              <div
                                className={`source-card ${
                                  option.disabled ? "disabled" : ""
                                } ${selectedSource === option.id ? "selected" : ""}`}
                                onClick={option.disabled ? undefined : option.action}
                                style={
                                  { "--card-color": option.color } as React.CSSProperties
                                }
                              >
                                <div className="source-icon">
                                  <FontAwesomeIcon icon={option.icon} />
                                </div>
                                <h5 className="source-title">{option.title}</h5>
                                <p className="source-description">{option.description}</p>
                                {option.disabled && (
                                  <div className="disabled-overlay">
                                    <small>
                                      {intl.formatMessage({
                                        id: "app.modernImageForm.insufficientPlan",
                                      })}
                                    </small>
                                  </div>
                                )}
                              </div>
                            )}
                          </Col>
                        ))}
                      </Row>
                    </div>

                    {/* Drag and Drop Area */}
                    <div 
                      className={`drag-drop-area ${isDragOver ? 'drag-over' : ''} ${selectedSource === 'upload' ? 'active' : ''}`}
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onDrop={handleDrop}
                      onClick={handleUploadClick}
                    >
                      <div className="drag-content">
                        <FontAwesomeIcon icon={faCloudUploadAlt} size="3x" className="mb-3" />
                        <h4>{intl.formatMessage({ id: "app.modernImageForm.dragTitle" })}</h4>
                        <p>{intl.formatMessage({ id: "app.modernImageForm.dragSubtitle" })}</p>
                        <small className="text-muted">
                          {intl.formatMessage({ id: "app.modernImageForm.dragFormats" }, { maxSize: Math.round(maxSizeImage / (1024 * 1024)) })}
                        </small>
                      </div>
                    </div>

                    {/* Hidden Native File Input */}
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      multiple={!singleImage}
                      onChange={handleFileInputChange}
                      style={{ display: 'none' }}
                    />

                    {/* Selected Images Preview */}
                    {images.length > 0 && (
                      <div className="selected-images mt-4">
                        <h5 className="mb-2">
                          <FontAwesomeIcon icon={faCheck} className="me-2 text-success" />
                          {intl.formatMessage({ id: "app.modernImageForm.selectedImages" }, { count: images.length })}
                        </h5>
                        {images.length > 1 ? (
                          <p className="social-post-reorder-hint mb-3">
                            {intl.formatMessage({ id: "app.modernImageForm.reorderHint" })}
                          </p>
                        ) : null}
                        <div className="images-grid">
                          <SocialPostImagesDnd
                            images={images}
                            onReorder={reorderImages}
                          />
                        </div>
                      </div>
                    )}
                  </>
              )}
            </div>
          </Col>
        </Row>
      </Container>

      {/* Image Bank Modal */}
      <ImageBankModal
        show={showImageBankModal}
        onHide={() => setShowImageBankModal(false)}
        onImageSelect={handleImageBankSelect}
      />

      {!CANVA_COMING_SOON && (
        <>
          <CanvaConnectModal
            isOpen={showCanvaConnectModal}
            toggle={() => setShowCanvaConnectModal(false)}
            onConnected={() => setShowCanvaDesignPicker(true)}
          />

          <CanvaDesignPickerModal
            isOpen={showCanvaDesignPicker}
            toggle={() => setShowCanvaDesignPicker(false)}
            onExported={applyCanvaExport}
          />
        </>
      )}
    </div>
  );
}
